import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from '@multaqa/i18n'
import { Button } from '@multaqa/ui/components/ui/button'
import { Checkbox } from '@multaqa/ui/components/ui/checkbox'
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@multaqa/ui/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@multaqa/ui/components/ui/form'
import { Input } from '@multaqa/ui/components/ui/input'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useAddSupplierContact } from '../hooks/use-suppliers'
import { type SupplierContactFormValues, supplierContactFormSchema } from '../schemas'

export function SupplierContactDialog({
  supplierId,
  open,
  onOpenChange,
}: {
  supplierId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const addContact = useAddSupplierContact()

  const schema = useMemo(() => supplierContactFormSchema(t), [t])

  const form = useForm<SupplierContactFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      role: '',
      phone: '',
      email: '',
      is_primary: false,
    },
  })

  const onSubmit = (values: SupplierContactFormValues) => {
    addContact.mutate(
      {
        supplierId,
        data: {
          name: values.name,
          role: values.role || null,
          phone: values.phone || null,
          email: values.email || null,
          is_primary: values.is_primary,
        },
      },
      {
        onSuccess: () => {
          form.reset()
          onOpenChange(false)
        },
      },
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent closeLabel={t('action.cancel')}>
        <DialogHeader>
          <DialogTitle>{t('contact.addTitle')}</DialogTitle>
          <DialogDescription>{t('contact.addDescription')}</DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col">
            <DialogBody className="space-y-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contact.name')}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="role"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('contact.role')}</FormLabel>
                    <FormControl>
                      <Input {...field} value={field.value ?? ''} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('contact.phone')}</FormLabel>
                      <FormControl>
                        <Input {...field} value={field.value ?? ''} dir="ltr" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('contact.email')}</FormLabel>
                      <FormControl>
                        <Input type="email" {...field} value={field.value ?? ''} dir="ltr" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="is_primary"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center gap-2 space-y-0">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={(checked) => field.onChange(checked === true)}
                      />
                    </FormControl>
                    <FormLabel className="cursor-pointer">{t('contact.isPrimary')}</FormLabel>
                  </FormItem>
                )}
              />
            </DialogBody>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('action.cancel')}
              </Button>
              <Button type="submit" loading={addContact.isPending}>
                {t('action.save')}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
